// @flow
import React, { PureComponent } from 'react';
import type { Element } from 'react';
import { connect } from 'react-redux';

import { Bling as GPT } from 'react-gpt';
import debounce from 'lodash.debounce';

import getFilterBreakdownProp from 'Common/getFilterBreakdownProp';
import getQueryString from 'Common/getQueryString';
import buildUrlFromForm from 'Common/buildUrlFromForm';
import {
  gtmBasicOptions,
  gtmFurtherOptions,
  gtmWinners,
  gtmPageview
} from 'Common/gtmPayloads';
import {
  slots,
  createAdUnitPath,
  targetingHome,
  adUnitPageNames
} from 'Common/ads';
import scrollToTopOfPage from 'Common/scrollToTopOfPage';
import isWindow from 'Common/isWindow';
import isWindowProp from 'Common/isWindowProp';
import { gtmEventPush } from 'utils/gtm/events';
import getClassifiedsSite from 'utils/getClassifiedsSite';

import * as Actions from 'actionTypes';
import metadata from 'App/variables/seo';

import { Grid, Row, Col } from 'Common/Grid';
import Accordion from 'Common/Accordion';
import BrowseAll from 'Common/BrowseAll';
import SEO from 'Common/Seo';
import ErrorBox from 'Common/ErrorBox';
import SearchForm from './SearchForm';
import CardsCarousel from 'Common/CardsCarousel';

import styles from 'rootContainer/Home.scss';

const TEXTS = {
  whatcar: {
    title: 'Why buy with What Car?',
    description:
      'Car-buying can be complex, but at What Car? we’ve got more than 45 years of experience of helping ' +
      'people buy the best car for their budget. Every car here is from heycar, so they all come with a warranty, ' +
      'and are guaranteed to be less than eight years and 100,000 miles old, so you can buy with total confidence.'
  },
  autocar: {
    title: 'Why buy with Autocar?',
    description:
      'There has never been more choice than now when it comes to buying a car, which can make finding ' +
      "the right car for you a daunting process. But it's also an exciting one. You can trust Autocar to steer " +
      'you towards the very best car for your needs and budget - just as we have been doing since 1895. ' +
      'Every car here is from heycar, so they all come with a warranty, and are guaranteed to be less than ' +
      'eight years and 100,000 miles old, so you can buy with total confidence.'
  }
};

/**
 * <Home /> component.
 * @param {string} postcode - postcode
 * @return {class} returns home instance
 */
export class Home extends PureComponent<*, *> {
  currentBounce: Function;
  debounce: Function;

  /**
   * @param {object} props - props passed
   */
  constructor(props: Object) {
    super(props);
    this.state = {
      showFurtherOptions: false
    };
  }

  /**
   * MapStateToProps implementation.
   * @return {object} new state object
   * @param {object} state object
   */
  static mapStateToProps(state: Object) {
    /**
     * pluckFromState
     * @param {string} prop - prop
     * @param {string} key - key
     * @return {array} - options
     */
    const pluckFromState = (prop, key) =>
      getFilterBreakdownProp(state, prop, key);
    const additonalOptions = {
      fuelTypes: pluckFromState('fuelType'),
      category: pluckFromState('category'),
      colours: pluckFromState('colour'),
      doors: pluckFromState('doors'),
      seats: pluckFromState('no_of_seats'),
      transmissions: pluckFromState('transmission'),
      mileages: pluckFromState('mileage'),
      annualTax: pluckFromState('annualTax'),
      fuelConsumption: pluckFromState('ec_combined_mpg'),
      co2Emissions: pluckFromState('co2_gkm'),
      acceleration: pluckFromState('0_to_62_mph_secs'),
      insuranceGroup: pluckFromState('insuranceGroup'),
      yearFrom: pluckFromState('yearFrom'),
      yearTo: pluckFromState('yearTo'),
      engineSizeFrom: pluckFromState('badge_engine_ccFrom'),
      engineSizeTo: pluckFromState('badge_engine_ccTo')
    };

    return {
      makes: pluckFromState('make'),
      models: pluckFromState('model'),
      priceFrom: pluckFromState('cashPriceStat', 'min'),
      priceTo: pluckFromState('cashPriceStat', 'max'),
      additonalOptions,
      form: state.form.search,
      filterForm: state.form.search,
      ui: state.ui,
      results: state.results,
      isResultsLoading: state.results.isLoading,
      isUILoading: state.ui.isLoading,
      isError: state.ui.error,
      popularCars: state.popular.results,
      isMobile: state.viewport.isXs
    };
  }

  /* eslint-disable camelcase */
  /**
   * UNSAFE_componentWillMount it fetches the makes.
   * @return {void}
   */
  UNSAFE_componentWillMount() {
    if (!this.props.filterForm) {
      this.props.dispatch({
        type: Actions.FETCH_MAKES
      });
    }

    this.props.dispatch({
      type: Actions.FETCH_POPULAR_RESULTS,
      query:
        'used=true&nearlynew=true&cashPriceFrom=15000&cashPriceTo=20000&pageSize=18'
    });
  }

  componentDidMount() {
    GPT.enableSingleRequest();
    targetingHome();
    this.interval = setInterval(this.pushPv, 300);
  }

  componentWillUnmount() {
    clearInterval(this.interval);
    if (isWindow() && isWindowProp('dataLayer')) {
      window.dataLayer.push({ page: '' });
    }
  }

  pushPv = () => {
    if (isWindow() && isWindowProp('dataLayer') && isWindowProp('location')) {
      window.dataLayer.push(
        gtmPageview(
          'Home',
          global.window.location.pathname,
          'Classifieds Landing Page'
        )
      );
      clearInterval(this.interval);
    }
  };

  submit = () => {
    if (isWindow() && isWindowProp('dataLayer')) {
      if (this.state.showFurtherOptions) {
        window.dataLayer.push(gtmFurtherOptions(this.props.form.values));
      } else {
        window.dataLayer.push(gtmBasicOptions(this.props.form.values));
      }
    }
    const url = buildUrlFromForm(this.props.form);
    this.props.history.push(url);
  };

  pushGtm = (tag, title) => {
    if (isWindow() && isWindowProp('dataLayer')) {
      const titleAndYear = title.split('(');
      window.dataLayer.push(
        gtmWinners(tag, titleAndYear[0], titleAndYear[1].slice(0, -1))
      );
    }
  };

  postCodeCheck = (postcode: String) => {
    this.props.dispatch({
      type: Actions.CHECK_POSTCODE,
      postcode
    });
    this.debounce = null;
  };

  debouncedFunc = fn => {
    if (!this.debounce) {
      this.debounce = debounce(fn, 1000);
    }
    return this.debounce;
  };

  checkPostcode = () => {
    const postcode =
      this.props.form.values && this.props.form.values.postCode
        ? this.props.form.values.postCode
        : null;

    if (postcode) {
      this.currentBounce = this.debouncedFunc(this.postCodeCheck);
      this.currentBounce(postcode);
    }
  };

  isPostcodeValid = () => {
    const postcode =
      this.props.form &&
      this.props.form.values &&
      this.props.form.values.postCode
        ? this.props.form.values.postCode
        : '';

    if (
      !postcode ||
      (this.props.ui.lastPostCode && this.props.ui.lastPostCode !== postcode)
    ) {
      return false;
    } else if (
      this.props.form &&
      this.props.form.values &&
      (this.props.form.values.isPostCodeValid === false ||
        this.props.form.values.isPostCodeValid === true)
    ) {
      return this.props.form.values.isPostCodeValid;
    } else {
      return false;
    }
  };

  getResults = () => {
    if (!this.props.form.values.postCode) {
      delete this.props.form.values.sortBy;
    }

    this.currentBounce = this.debouncedFunc(() => {
      const query = getQueryString(this.props.form);
      this.props.dispatch({
        type: Actions.FETCH_RESULTS,
        query
      });
      this.debounce = null;
    });
    this.currentBounce();
  };

  toggleFurtherOptions = () => {
    gtmEventPush({
      method: 'filterForm',
      args: {
        itemLabel: !this.state.showFurtherOptions
          ? 'More options'
          : 'Less options'
      }
    });
    this.setState(
      {
        showFurtherOptions: !this.state.showFurtherOptions
      },
      () => (!this.state.showFurtherOptions ? scrollToTopOfPage() : null)
    );
  };

  /**
   * Renders the Home component.
   *
   * @return {JSX} - rendered Home page.
   */
  render(): Element<*> {
    const {
      ui,
      makes,
      models,
      results,
      form,
      priceFrom,
      priceTo,
      additonalOptions,
      isResultsLoading,
      isUILoading,
      isError,
      popularCars,
      isMobile
    } = this.props;

    const resultsLength =
      results && results.pagination ? results.pagination.total : 0;
    const fields = form ? { ...form.values } : {};

    // Persist postcode
    if (ui.lastPostCode) {
      fields['postCode'] = ui.lastPostCode;
    }

    const adProps = {
      page: adUnitPageNames['home']
    };

    const carouselItemsToDisplay = { xs: 1, sm: 2, md: 2, lg: 3 };
    const classifiedsSite = getClassifiedsSite();
    return (
      <Grid>
        <SEO {...metadata.home()} />
        {isError && (
          <Row>
            <Col colXs={12} colSm={12} colMd={12} colLg={12}>
              <ErrorBox type={500} />
            </Col>
          </Row>
        )}
        {!isError && (
          <Row>
            <Col colXs={12} colSm={12} colMd={12} colLg={12}>
              {typeof window !== 'undefined' && window.document && (
                <SearchForm
                  onSubmit={this.submit}
                  getResults={this.getResults}
                  checkPostcode={this.checkPostcode}
                  fields={fields}
                  makes={makes}
                  models={models}
                  additonalOptions={additonalOptions}
                  resultsLength={resultsLength}
                  toggleFurtherOptions={this.toggleFurtherOptions}
                  showFurtherOptions={
                    this.state && this.state.showFurtherOptions
                  }
                  isPostcodeValid={this.isPostcodeValid()}
                  priceFrom={priceFrom}
                  priceTo={priceTo}
                  isResultsLoading={isResultsLoading}
                  isUILoading={isUILoading}
                />
              )}
              <div className={styles.headerAd}>
                <GPT
                  id={slots.header.id}
                  adUnitPath={createAdUnitPath(adProps)}
                  collapseEmptyDiv={true}
                  renderWhenViewable={false}
                  sizeMapping={slots.header.sizeMapping}
                  targeting={slots.header.targeting}
                />
              </div>
            </Col>
          </Row>
        )}
        {!isError && (
          <Row>
            <Col colXs={12} colSm={12} colMd={9} colLg={9}>
              <section className={styles.intro}>
                <h3 className={styles.heading}>
                  {TEXTS[classifiedsSite]['title']}
                </h3>
                <p className={styles.p}>
                  {TEXTS[classifiedsSite]['description']}
                </p>
              </section>
              {!isError && popularCars && (
                <CardsCarousel
                  carouselItemsToDisplay={carouselItemsToDisplay}
                  extendStyle={styles.classDetailCarouselItems}
                  title={'Popular cars'}
                  history={this.props.history}
                  related={isMobile ? popularCars.slice(0, 10) : popularCars}
                  gtmSectionName={'popularCars'}
                />
              )}
            </Col>
            <Col colXs={12} colSm={12} colMd={3} colLg={3}>
              <div className={styles.sideAd}>
                <GPT
                  id={slots.sidenav.id}
                  adUnitPath={createAdUnitPath(adProps)}
                  collapseEmptyDiv={true}
                  renderWhenViewable={false}
                  sizeMapping={slots.sidenav.sizeMapping}
                  targeting={slots.sidenav.targeting}
                />
              </div>
            </Col>
          </Row>
        )}
        {!isError && (
          <Row>
            <Col colXs={12} className={styles.mtl}>
              <Accordion
                accordionHeight={200}
                gtmSectionName={'accordionBrowse'}
              >
                <BrowseAll
                  items={makes}
                  text="makes"
                  history={this.props.history}
                />
              </Accordion>
            </Col>
          </Row>
        )}
      </Grid>
    );
  }
}

export default connect(Home.mapStateToProps)(Home);



// WEBPACK FOOTER //
// ./src/Home/index.js